import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  Calendar as CalendarIcon,
  ChevronRight,
  Loader2,
  MessageSquare,
  Package,
  Search,
  SlidersHorizontal,
  X,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

import type { OrderCard, OrderListResponse, RefName } from "@/api/order_check";
import { DateCalendarDialog } from "@/components/CalendarDialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { fmtDate, ruNum as num, ruRub as rub } from "@/lib/format";
import {
  fmtShortDate,
  isISODate,
  parseStoredShipFilter,
  resolveShipFilter,
  shipFilterLabel,
  type ShipFilter,
} from "@/lib/ship-date-filter";
import { useDebounced } from "@/lib/use-debounced";
import { cn } from "@/lib/utils";

/**
 * Список заказов покупателя — ОДИН на все разделы, которые его показывают.
 *
 * Проверка заказов и раскладка паллет показывают один и тот же список с теми
 * же фильтрами. Вторая копия разъехалась бы с первой на первом же уточнении —
 * так уже случилось с карточкой позиции в шторках сборки, где одна рисовала
 * артикул, а вторая нет.
 *
 * Отличаются только ручки (право у разделов своё), ключи localStorage, куда
 * ведёт тап и что нарисовано справа на карточке.
 */
export interface OrdersListProps {
  title: string;
  subtitle?: string;
  /** Префикс ключей localStorage: фильтры у разделов свои. */
  lsPrefix: string;
  /** Префикс ключей react-query — иначе разделы делили бы кэш. */
  queryKey: string;
  /** Ключ кэша организаций. Отдельный, потому что список организаций от вида
   * заказа НЕ зависит: одна ручка, один белый список. Уехав в общий префикс,
   * он форкался бы вместе с ним и перечитывался из 1С на каждой вкладке. */
  orgsKey?: string;
  api: {
    list: (p: {
      q?: string;
      organization_ref?: string;
      state_ref?: string;
      check_status?: string;
      /** Дата отгрузки `yyyy-mm-dd`; пусто — любая. */
      ship_date?: string;
      skip?: number;
    }) => Promise<OrderListResponse>;
    organizations: () => Promise<RefName[]>;
    states: () => Promise<RefName[]>;
  };
  onOpen: (order: OrderCard) => void;
  /** Кнопки справа на карточке (у проверки — сборочное задание). */
  actions?: (order: OrderCard) => React.ReactNode;
  /** Переключатель под заголовком (у проверки — вид заказа). */
  tabs?: React.ReactNode;
}
const lsGet = (k: string) => {
  try {
    return localStorage.getItem(k) ?? "";
  } catch {
    return "";
  }
};

// Фильтр по статусу проверки сборки.
const CHECK_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Все" },
  { value: "none", label: "Не проверен" },
  { value: "in_progress", label: "В работе" },
  { value: "completed", label: "Проверен" },
];

export function OrdersList({
  title,
  subtitle,
  lsPrefix,
  queryKey,
  orgsKey,
  api,
  onOpen,
  actions,
  tabs,
}: OrdersListProps): React.ReactElement {
  const [rawQuery, setRawQuery] = useState(() => lsGet(`${lsPrefix}_q`));
  const query = useDebounced(rawQuery.trim(), 350);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [orgRef, setOrgRef] = useState<string>(() => lsGet(`${lsPrefix}_org`));
  const [stateRef, setStateRef] = useState<string>(() => lsGet(`${lsPrefix}_state`));
  const [checkStatus, setCheckStatus] = useState<string>(() => lsGet(`${lsPrefix}_check`));
  // Пресеты хранятся относительно (`today`/`tomorrow`), в ручку — конкретная
  // дата на момент рендера: см. `lib/ship-date-filter.ts`.
  const [shipFilter, setShipFilter] = useState<ShipFilter>(() =>
    parseStoredShipFilter(lsGet(`${lsPrefix}_ship`)),
  );
  const shipDate = resolveShipFilter(shipFilter);
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(`${lsPrefix}_q`, rawQuery);
      localStorage.setItem(`${lsPrefix}_org`, orgRef);
      localStorage.setItem(`${lsPrefix}_state`, stateRef);
      localStorage.setItem(`${lsPrefix}_check`, checkStatus);
      localStorage.setItem(`${lsPrefix}_ship`, shipFilter);
    } catch {
      /* приватный режим — не критично */
    }
  }, [rawQuery, orgRef, stateRef, checkStatus, shipFilter]);

  const orgsQ = useQuery({
    queryKey: [`${orgsKey ?? queryKey}-orgs`],
    queryFn: () => api.organizations(),
    enabled: filtersOpen,
    staleTime: 5 * 60_000,
  });
  const statesQ = useQuery({
    queryKey: [`${queryKey}-states`],
    queryFn: () => api.states(),
    enabled: filtersOpen,
    staleTime: 5 * 60_000,
  });

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
  } = useInfiniteQuery({
    queryKey: [`${queryKey}-orders`, query, orgRef, stateRef, checkStatus, shipDate],
    queryFn: ({ pageParam }) =>
      api.list({
        q: query,
        organization_ref: orgRef,
        state_ref: stateRef,
        check_status: checkStatus,
        ship_date: shipDate,
        skip: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.has_more
        ? allPages.reduce((n, p) => n + p.items.length, 0)
        : undefined,
  });

  const orders = data?.pages.flatMap((p) => p.items) ?? [];
  const activeFilters =
    (orgRef ? 1 : 0) + (stateRef ? 1 : 0) + (checkStatus ? 1 : 0) + (shipDate ? 1 : 0);

  // Бесконечная подгрузка: подгружаем следующую страницу, когда виден сентинел.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    });
    io.observe(el);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="mx-auto max-w-xl space-y-3">
      <div>
        <h1 className="text-xl font-semibold">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {tabs}

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={rawQuery}
            onChange={(e) => setRawQuery(e.target.value)}
            placeholder="Номер или контрагент"
            className="pl-8"
          />
          {rawQuery && (
            <button
              type="button"
              onClick={() => setRawQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
              aria-label="Очистить"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
        <Button
          variant={activeFilters ? "default" : "outline"}
          size="icon"
          onClick={() => setFiltersOpen((s) => !s)}
          aria-label="Фильтры"
          className="relative shrink-0"
        >
          <SlidersHorizontal className="size-4" />
          {activeFilters > 0 && (
            <span className="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground">
              {activeFilters}
            </span>
          )}
        </Button>
      </div>

      {/* Дата отгрузки видна и при свёрнутой панели: список, отфильтрованный
          по дню, легко принять за «все заказы». */}
      {shipDate && (
        <div className="flex">
          <span className="inline-flex h-9 items-center rounded-full border border-primary bg-primary/10 pl-3 text-sm text-primary">
            <button type="button" onClick={() => setFiltersOpen(true)}>
              {shipFilterLabel(shipFilter)}
            </button>
            <button
              type="button"
              onClick={() => setShipFilter("")}
              className="flex size-9 items-center justify-center"
              aria-label="Сбросить дату отгрузки"
            >
              <X className="size-4" />
            </button>
          </span>
        </div>
      )}

      {filtersOpen && (
        <Card className="space-y-3 p-3">
          <div>
            <div className="mb-1 text-xs font-medium text-muted-foreground">
              Дата отгрузки
            </div>
            <div className="grid grid-cols-4 gap-1">
              <SegButton active={shipFilter === ""} onClick={() => setShipFilter("")}>
                Любая
              </SegButton>
              <SegButton active={shipFilter === "today"} onClick={() => setShipFilter("today")}>
                Сегодня
              </SegButton>
              <SegButton
                active={shipFilter === "tomorrow"}
                onClick={() => setShipFilter("tomorrow")}
              >
                Завтра
              </SegButton>
              <SegButton
                active={isISODate(shipFilter)}
                onClick={() => setCalendarOpen(true)}
              >
                <CalendarIcon className="size-3.5 shrink-0" />
                {isISODate(shipFilter) ? fmtShortDate(shipFilter) : "Дата…"}
              </SegButton>
            </div>
          </div>
          <div>
            <div className="mb-1 text-xs font-medium text-muted-foreground">
              Статус проверки
            </div>
            <div className="flex flex-wrap gap-1.5">
              {CHECK_OPTIONS.map((o) => (
                <Chip
                  key={o.value || "all"}
                  active={checkStatus === o.value}
                  onClick={() => setCheckStatus(o.value)}
                >
                  {o.label}
                </Chip>
              ))}
            </div>
          </div>
          <div>
            <div className="mb-1 text-xs font-medium text-muted-foreground">
              Организация
            </div>
            <div className="flex flex-wrap gap-1.5">
              {orgsQ.isLoading && (
                <Loader2 className="size-4 animate-spin text-muted-foreground" />
              )}
              {orgsQ.data?.map((o) => (
                <Chip
                  key={o.ref}
                  active={orgRef === o.ref}
                  onClick={() => setOrgRef(orgRef === o.ref ? "" : o.ref)}
                >
                  {o.name}
                </Chip>
              ))}
            </div>
          </div>
          <div>
            <div className="mb-1 text-xs font-medium text-muted-foreground">
              Состояние
            </div>
            <div className="flex flex-wrap gap-1.5">
              {statesQ.isLoading && (
                <Loader2 className="size-4 animate-spin text-muted-foreground" />
              )}
              {statesQ.data?.map((s) => (
                <Chip
                  key={s.ref}
                  active={stateRef === s.ref}
                  onClick={() => setStateRef(stateRef === s.ref ? "" : s.ref)}
                >
                  {s.name}
                </Chip>
              ))}
            </div>
          </div>
          {activeFilters > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setOrgRef("");
                setStateRef("");
                setCheckStatus("");
                setShipFilter("");
              }}
            >
              Сбросить фильтры
            </Button>
          )}
        </Card>
      )}

      {isLoading && (
        <div className="flex justify-center py-10 text-muted-foreground">
          <Loader2 className="size-6 animate-spin" />
        </div>
      )}
      {isError && (
        <Card className="border-destructive/50 p-3 text-sm text-destructive">
          Не удалось загрузить заказы: {(error as Error)?.message ?? "ошибка"}
        </Card>
      )}
      {!isLoading && !isError && orders.length === 0 && (
        <p className="py-10 text-center text-sm text-muted-foreground">
          {query
            ? `По запросу «${query}» ничего не найдено`
            : shipDate
              ? `Нет заказов с отгрузкой ${fmtDate(shipDate)}`
              : "Нет заказов за последние 3 месяца"}
        </p>
      )}

      <div className="space-y-2">
        {orders.map((o) => (
          <OrderCardView
            key={o.ref}
            order={o}
            onClick={() => onOpen(o)}
            actions={actions?.(o)}
          />
        ))}
      </div>

      <div ref={sentinelRef} className="h-6" />
      {calendarOpen && (
        <DateCalendarDialog
          value={shipDate}
          onPick={(iso) => {
            setShipFilter(iso);
            setCalendarOpen(false);
          }}
          onClose={() => setCalendarOpen(false)}
          hint="Тапните дату отгрузки"
        />
      )}
      {isFetchingNextPage && (
        <div className="flex justify-center py-2 text-muted-foreground">
          <Loader2 className="size-5 animate-spin" />
        </div>
      )}
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-3 py-1 text-xs transition-colors",
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-input bg-background text-foreground hover:bg-accent",
      )}
    >
      {children}
    </button>
  );
}

/** Кнопка сегмента «Дата отгрузки»: 4 в ряд на ТСД (~360px), высота 36px. */
function SegButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "flex h-9 min-w-0 items-center justify-center gap-1 rounded-md border px-1 text-xs transition-colors",
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-input bg-background text-foreground hover:bg-accent",
      )}
    >
      {children}
    </button>
  );
}

function CheckBadge({ status }: { status: OrderCard["check_status"] }): React.ReactElement | null {
  if (status === "completed") {
    return (
      <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
        ✓ Проверен
      </span>
    );
  }
  if (status === "in_progress") {
    return (
      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
        В работе
      </span>
    );
  }
  return null;
}

function OrderCardView({
  order,
  onClick,
  actions,
}: {
  order: OrderCard;
  onClick: () => void;
  actions?: React.ReactNode;
}): React.ReactElement {
  return (
    <Card className="flex items-center gap-2 p-3 transition-colors hover:bg-accent">
      <button type="button" onClick={onClick} className="min-w-0 flex-1 space-y-1 text-left">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold">{order.number}</span>
          {/* Дата ОТГРУЗКИ, а не дата документа: заказ мог быть заведён неделю
              назад, а собирают его под конкретный день вывоза. */}
          <span className="text-xs text-muted-foreground">
            {order.ship_date ? `отгрузка ${fmtDate(order.ship_date)}` : fmtDate(order.date)}
          </span>
          {order.state_name && (
            <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
              {order.state_name}
            </span>
          )}
          <CheckBadge status={order.check_status} />
        </div>
        <div className="truncate text-sm text-muted-foreground">
          {order.kontragent_name}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Package className="size-3" /> {num.format(order.qty)} шт
          </span>
          <span>{rub.format(order.amount)} ₽</span>
          <span className="truncate">{order.organization_name}</span>
        </div>
        {order.comment && (
          <div className="flex items-start gap-1 text-xs text-muted-foreground">
            <MessageSquare className="mt-0.5 size-3 shrink-0" />
            <span className="line-clamp-2">{order.comment}</span>
          </div>
        )}
      </button>
      {actions}
      <button type="button" onClick={onClick} aria-label="Открыть заказ" className="shrink-0">
        <ChevronRight className="size-5 text-muted-foreground" />
      </button>
    </Card>
  );
}
